import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputBase,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import PublishIcon from '@mui/icons-material/Publish';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { API_BASE_URL, fetchJson } from './api';

const CATEGORY_LABELS = {
    construction: 'Construction Costs',
    professional_fees: 'Professional Fees',
    other_costs: 'Other Development Costs',
    contingency: 'Contingency'
};

const moneyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
});

const rateFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const TABLE_HEADER_NAVY = '#17324d';
const TABLE_TOTAL_GREY = '#8a96a3';
const tableTotalRowSx = {
    '& .MuiTableCell-root': { bgcolor: TABLE_TOTAL_GREY, color: '#fff', fontWeight: 900 }
};

function toNumber(value) {
    const normalized = String(value ?? '').replace(/[^0-9.-]/g, '');
    const number = Number(normalized);
    return normalized && Number.isFinite(number) ? number : 0;
}

function formatCurrencyCell(value) {
    const text = String(value ?? '').trim();
    if (!text || text.startsWith('=')) return text;
    return moneyFormatter.format(toNumber(text));
}

function sentence(value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    return /[.!?]$/.test(text) ? text : `${text}.`;
}

function AiBenchmarking({ proposal }) {
    const benchmark = proposal?.proposed_value && typeof proposal.proposed_value === 'object'
        ? proposal.proposed_value
        : null;
    const low = Number(benchmark?.benchmark_range_low);
    const high = Number(benchmark?.benchmark_range_high);
    const midpoint = Number(benchmark?.benchmark_cost_per_sqft);
    const submittedRate = Number(benchmark?.submitted_implied_cost_per_sqft);
    const hasRange = Number.isFinite(low) && low > 0 && Number.isFinite(high) && high > 0;

    return (
        <Box component="section" aria-labelledby="ai-benchmarking-heading" sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
            <Typography id="ai-benchmarking-heading" variant="h6" sx={{ fontWeight: 900, mb: 1 }}>AI-Benchmarking</Typography>
            {!benchmark ? (
                <Alert severity="info">No AI cost benchmark has been approved for this project.</Alert>
            ) : (
                <Stack spacing={1.25}>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase' }}>Acceptable construction cost range</Typography>
                        <Typography sx={{ mt: 0.25, fontSize: '1.25rem', fontWeight: 900 }}>
                            {hasRange ? `${rateFormatter.format(low)} to ${rateFormatter.format(high)} per sq ft` : 'Range not available'}
                        </Typography>
                        {Number.isFinite(midpoint) && midpoint > 0 ? (
                            <Typography variant="body2" color="text.secondary">Indicative midpoint: {rateFormatter.format(midpoint)} per sq ft.</Typography>
                        ) : null}
                        {Number.isFinite(submittedRate) && submittedRate > 0 ? (
                            <Typography variant="body2" color="text.secondary">Submitted implied rate: {rateFormatter.format(submittedRate)} per sq ft.</Typography>
                        ) : null}
                    </Box>
                    {sentence(proposal.rationale) ? <Typography variant="body2">{sentence(proposal.rationale)}</Typography> : null}
                    {sentence(benchmark.comparison_to_submitted) ? <Typography variant="body2"><strong>Comparison:</strong> {sentence(benchmark.comparison_to_submitted)}</Typography> : null}
                    {Array.isArray(benchmark.key_drivers) && benchmark.key_drivers.length ? (
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.4 }}>Key drivers</Typography>
                            {benchmark.key_drivers.map((driver, index) => <Typography key={`driver-${index}`} variant="body2" sx={{ mb: 0.4 }}>{sentence(driver)}</Typography>)}
                        </Box>
                    ) : null}
                    {Array.isArray(benchmark.caveats) && benchmark.caveats.length ? (
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.4 }}>Caveats</Typography>
                            {benchmark.caveats.map((caveat, index) => <Typography key={`caveat-${index}`} variant="body2" color="text.secondary" sx={{ mb: 0.4 }}>{sentence(caveat)}</Typography>)}
                        </Box>
                    ) : null}
                </Stack>
            )}
        </Box>
    );
}

function CurrencyFormulaInput({ value, onChange, ariaLabel }) {
    const [focused, setFocused] = useState(false);
    return (
        <InputBase
            fullWidth
            value={focused ? String(value ?? '') : formatCurrencyCell(value)}
            onFocus={(event) => {
                setFocused(true);
                window.requestAnimationFrame(() => event.target.select());
            }}
            onBlur={() => setFocused(false)}
            onChange={(event) => onChange(event.target.value)}
            inputProps={{ 'aria-label': ariaLabel, inputMode: 'decimal', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
            sx={{ px: 1, py: 0.85, fontSize: '0.88rem' }}
        />
    );
}

function EditableCurrencyCell({ value, onChange, ariaLabel, displayAsNegative = false }) {
    const [focused, setFocused] = useState(false);
    const displayValue = displayAsNegative && String(value ?? '').trim()
        ? moneyFormatter.format(-Math.abs(toNumber(value)))
        : formatCurrencyCell(value);
    return (
        <InputBase
            fullWidth
            value={focused ? String(value ?? '') : displayValue}
            onFocus={(event) => {
                setFocused(true);
                window.requestAnimationFrame(() => event.target.select());
            }}
            onBlur={() => setFocused(false)}
            onChange={(event) => onChange(event.target.value)}
            inputProps={{ 'aria-label': ariaLabel, inputMode: 'decimal', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
            sx={{ px: 1, py: 0.65, fontSize: '0.82rem' }}
        />
    );
}

function EditableRateInput({ value, fallback, onChange }) {
    const [focused, setFocused] = useState(false);
    const rawValue = String(value ?? '');
    const displayValue = rawValue.trim() || fallback === '' || fallback == null
        ? rawValue.trim() ? rateFormatter.format(toNumber(rawValue)) : ''
        : rateFormatter.format(Number(fallback));
    return (
        <TextField
            fullWidth
            size="small"
            label="Surveyor Assessed Cost (£/sq ft)"
            value={focused ? rawValue : displayValue}
            onFocus={(event) => {
                setFocused(true);
                window.requestAnimationFrame(() => event.target.select());
            }}
            onBlur={() => setFocused(false)}
            onChange={(event) => onChange(event.target.value)}
            inputProps={{ inputMode: 'decimal' }}
        />
    );
}

function normalizeCostKey(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function readObjectDrawdown(costs, field, row) {
    const values = costs?.[field] && typeof costs[field] === 'object' && !Array.isArray(costs[field]) ? costs[field] : {};
    if (row?.source?.system === 'confirmed_live_facility_balance' && Object.hasOwn(values, 'facility_balance')) {
        return toNumber(values.facility_balance);
    }
    const expected = row?.source?.field ? `${row.source.field}:${row.description}` : '';
    if (expected && Object.hasOwn(values, expected)) return toNumber(values[expected]);
    const description = normalizeCostKey(row?.description);
    const match = Object.entries(values).find(([key]) => normalizeCostKey(String(key).split(':').slice(1).join(':')) === description);
    return match ? toNumber(match[1]) : 0;
}

function confirmedBudgetTotal(developmentCosts, category) {
    if (!developmentCosts || typeof developmentCosts !== 'object') return null;
    const configs = {
        construction: 'constructionBudgetRows',
        professional_fees: 'professionalFeeBudgetRows',
        other_costs: 'additionalCostBudgetRows',
        contingency: 'contingencyBudgetRows'
    };
    if (category === 'contingency') {
        const explicit = String(developmentCosts.contingencyBudgetTotal ?? '').trim();
        if (explicit) return toNumber(explicit);
    }
    const rows = developmentCosts[configs[category]];
    if (!Array.isArray(rows)) return null;
    return rows.reduce((sum, row) => sum + toNumber(row?.budget), 0);
}

function buildDrawdownSections(workbook, calculated, drawdowns, liveDevelopmentCosts = null) {
    const periods = Array.isArray(drawdowns) ? drawdowns : [];
    const configs = [
        { category: 'construction', title: 'Construction Costs', field: 'dd_construction' },
        { category: 'contingency', title: 'Additional Costs (Contingency Expenditure)', field: 'contingency_claims' },
        { category: 'professional_fees', title: 'Professional Fees', field: 'dd_professional_fees' },
        { category: 'other_costs', title: 'Other Development Costs', field: 'dd_additional_costs' }
    ];
    return configs.map((config) => {
        const category = workbook?.categories?.find((candidate) => candidate.key === config.category);
        const rows = (category?.rows || []).map((row) => {
            const borrower = toNumber(calculated?.row_values?.[row.id] ?? row.surveyor_assessment ?? row.borrower_budget);
            const addOmit = Object.hasOwn(row, 'drawdown_add_omit') ? toNumber(row.drawdown_add_omit) : 0;
            const values = {};
            for (const period of periods) {
                const costs = period?.drawdown_costs || {};
                if (config.field === 'contingency_claims') {
                    const description = normalizeCostKey(row.description);
                    values[period.drawdown_number] = (Array.isArray(costs.contingency_claims) ? costs.contingency_claims : [])
                        .filter((claim) => normalizeCostKey(claim?.description) === description)
                        .reduce((sum, claim) => sum + toNumber(claim?.value), 0);
                } else {
                    values[period.drawdown_number] = readObjectDrawdown(costs, config.field, row);
                }
            }
            const expenditure = Object.values(values).reduce((sum, value) => sum + value, 0);
            return {
                ...row,
                borrower,
                addOmit,
                forecast: borrower + addOmit,
                values,
                expenditure,
                complete: borrower + addOmit > 0 ? (expenditure / (borrower + addOmit)) * 100 : 0,
                costToComplete: borrower + addOmit - expenditure
            };
        });
        const liveTotal = confirmedBudgetTotal(liveDevelopmentCosts, config.category);
        const initialTotal = rows.reduce((sum, row) => sum + row.borrower, 0);
        const facilityBalance = liveTotal == null ? 0 : Math.round((liveTotal - initialTotal) * 100) / 100;
        if (Math.abs(facilityBalance) >= 0.01) {
            const row = {
                id: `facility-balance-${config.category}`,
                description: 'Facility Balance',
                borrower_budget: facilityBalance,
                borrower: facilityBalance,
                addOmit: toNumber(workbook?.summary?.facility_balance_add_omit?.[config.category]),
                forecast: facilityBalance + toNumber(workbook?.summary?.facility_balance_add_omit?.[config.category]),
                values: {},
                source: { system: 'confirmed_live_facility_balance', category: config.category }
            };
            for (const period of periods) {
                const costs = period?.drawdown_costs || {};
                if (config.field === 'contingency_claims') {
                    row.values[period.drawdown_number] = (Array.isArray(costs.contingency_claims) ? costs.contingency_claims : [])
                        .filter((claim) => normalizeCostKey(claim?.description) === 'facility balance')
                        .reduce((sum, claim) => sum + toNumber(claim?.value), 0);
                } else {
                    row.values[period.drawdown_number] = readObjectDrawdown(costs, config.field, row);
                }
            }
            row.expenditure = Object.values(row.values).reduce((sum, value) => sum + value, 0);
            row.complete = row.forecast > 0 ? row.expenditure / row.forecast * 100 : 0;
            row.costToComplete = row.forecast - row.expenditure;
            rows.push(row);
        }
        return { ...config, rows };
    });
}

function sumRows(rows, field) {
    return rows.reduce((sum, row) => sum + toNumber(row[field]), 0);
}

function buildDrawdownSummary(sections, drawdownNumbers, summary = {}) {
    const sectionRows = sections.map((section) => {
        const original = sumRows(section.rows, 'borrower');
        const addOmit = sumRows(section.rows, 'addOmit');
        const forecast = sumRows(section.rows, 'forecast');
        const values = Object.fromEntries(drawdownNumbers.map((number) => [number, section.rows.reduce((sum, row) => sum + toNumber(row.values[number]), 0)]));
        const expenditure = Object.values(values).reduce((sum, value) => sum + value, 0);
        return { key: section.category, label: section.title, original, addOmit, forecast, values, expenditure, complete: forecast > 0 ? expenditure / forecast * 100 : 0, costToComplete: forecast - expenditure };
    });
    const total = {
        key: 'development-total',
        label: 'Total Development Cost',
        original: sumRows(sectionRows, 'original'),
        addOmit: sumRows(sectionRows, 'addOmit'),
        forecast: sumRows(sectionRows, 'forecast'),
        values: Object.fromEntries(drawdownNumbers.map((number) => [number, sectionRows.reduce((sum, row) => sum + toNumber(row.values[number]), 0)]))
    };
    total.expenditure = Object.values(total.values).reduce((sum, value) => sum + value, 0);
    total.complete = total.forecast > 0 ? total.expenditure / total.forecast * 100 : 0;
    total.costToComplete = total.forecast - total.expenditure;

    const borrowerInputAmount = toNumber(summary.borrower_input);
    const borrowerInputByDrawdown = summary.borrower_input_drawdowns && typeof summary.borrower_input_drawdowns === 'object'
        ? summary.borrower_input_drawdowns
        : {};
    const borrowerInput = {
        key: 'borrower-input',
        label: 'Borrower Input',
        original: -borrowerInputAmount,
        addOmit: 0,
        forecast: -borrowerInputAmount,
        values: Object.fromEntries(drawdownNumbers.map((number) => [number, -toNumber(borrowerInputByDrawdown[number])]))
    };
    borrowerInput.expenditure = Object.values(borrowerInput.values).reduce((sum, value) => sum + value, 0);
    borrowerInput.complete = borrowerInputAmount > 0 ? Math.abs(borrowerInput.expenditure) / borrowerInputAmount * 100 : 0;
    borrowerInput.costToComplete = borrowerInput.forecast - borrowerInput.expenditure;

    const facility = {
        key: 'facility-total',
        label: 'Facility / Drawdown Sum',
        original: total.original + borrowerInput.original,
        addOmit: total.addOmit,
        forecast: total.forecast + borrowerInput.forecast,
        values: Object.fromEntries(drawdownNumbers.map((number) => [number, total.values[number] + borrowerInput.values[number]]))
    };
    facility.expenditure = Object.values(facility.values).reduce((sum, value) => sum + value, 0);
    facility.complete = facility.forecast > 0 ? facility.expenditure / facility.forecast * 100 : 0;
    facility.costToComplete = facility.forecast - facility.expenditure;
    return { sectionRows, total, borrowerInput, facility, borrowerInputByDrawdown };
}

function AddOmitWarningCell({ value, acknowledgedValue, onDecision }) {
    const roundedValue = Math.round(toNumber(value) * 100) / 100;
    const roundedAcknowledgedValue = Math.round(toNumber(acknowledgedValue) * 100) / 100;
    const requiresAcknowledgement = roundedValue !== 0 && roundedAcknowledgedValue !== roundedValue;
    if (!requiresAcknowledgement) return <TableCell align="right">{moneyFormatter.format(roundedValue)}</TableCell>;

    return (
        <TableCell
            align="right"
            sx={{ bgcolor: '#facc15 !important', color: '#111827 !important', fontWeight: '900 !important', p: 0 }}
        >
            <Tooltip
                arrow
                placement="top"
                title={(
                    <Stack spacing={1} sx={{ p: 0.5, maxWidth: 280 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            The Add/Omit Column is not balanced to £0, is this correct?
                        </Typography>
                        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                            <Button size="small" variant="contained" color="inherit" onClick={() => onDecision(roundedValue)}>Yes</Button>
                            <Button size="small" variant="outlined" color="inherit" onClick={() => onDecision(null)}>No</Button>
                        </Stack>
                    </Stack>
                )}
            >
                <Box
                    component="span"
                    tabIndex={0}
                    aria-label="Unbalanced Add/Omit total"
                    sx={{ display: 'block', px: 2, py: 0.75, cursor: 'help' }}
                >
                    {moneyFormatter.format(roundedValue)}
                </Box>
            </Tooltip>
        </TableCell>
    );
}

const worksheetTableSx = {
    minWidth: 840,
    tableLayout: 'fixed',
    '& .MuiTableCell-root:not(:last-of-type)': { borderRight: '1px solid #d9dee7' },
    '& .MuiTableHead-root .MuiTableCell-root': {
        bgcolor: TABLE_HEADER_NAVY,
        color: '#fff',
        fontWeight: 900,
        borderColor: alpha('#fff', 0.22)
    },
    '& .MuiTableCell-alignRight': { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
    '& .cost-input-cell:focus-within': { bgcolor: alpha('#2563eb', 0.06), outline: '2px solid #2563eb', outlineOffset: '-2px' }
};

function DrawdownTables({ workbook, calculated, drawdowns, loading, error, interimNumber, liveDevelopmentCosts, onUpdateAddOmit, onUpdateAddOmitAcknowledgement, onUpdateBorrowerInput, onUpdateBorrowerInputDrawdown }) {
    if (loading) return <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    const drawdownNumbers = Array.from(new Set([
        ...(drawdowns || []).map((item) => Number(item.drawdown_number)).filter(Boolean),
        Number(interimNumber) || 1
    ])).sort((a, b) => a - b);
    const sections = buildDrawdownSections(workbook, calculated, drawdowns, liveDevelopmentCosts);
    const summary = buildDrawdownSummary(sections, drawdownNumbers, workbook.summary);
    const moneyColumnWidth = 96;
    const drawdownColumnWidth = 200;
    const percentColumnWidth = 82;
    const descriptionColumnWidth = 320;
    const minimumTableWidth = descriptionColumnWidth + (5 * moneyColumnWidth) + (drawdownNumbers.length * drawdownColumnWidth) + percentColumnWidth;
    const renderSummaryRow = (row, tone = 'default') => (
        <TableRow key={row.key} sx={tone === 'total' ? tableTotalRowSx : tone === 'facility' ? { '& .MuiTableCell-root': { bgcolor: TABLE_HEADER_NAVY, color: '#fff', fontWeight: 900 } } : undefined}>
            <TableCell sx={{ fontWeight: tone === 'default' ? 700 : 900 }}>{row.label}</TableCell>
            <TableCell align="right" className={row.key === 'borrower-input' ? 'cost-input-cell' : undefined} sx={row.key === 'borrower-input' ? { p: 0 } : undefined}>
                {row.key === 'borrower-input'
                    ? <EditableCurrencyCell ariaLabel="Borrower Input" value={workbook.summary?.borrower_input ?? ''} onChange={onUpdateBorrowerInput} displayAsNegative />
                    : moneyFormatter.format(row.original)}
            </TableCell>
            {row.key === 'development-total'
                ? <AddOmitWarningCell value={row.addOmit} acknowledgedValue={workbook.summary?.drawdown_add_omit_acknowledged_total} onDecision={onUpdateAddOmitAcknowledgement} />
                : <TableCell align="right">{moneyFormatter.format(row.addOmit)}</TableCell>}
            <TableCell align="right">{moneyFormatter.format(row.forecast)}</TableCell>
            {drawdownNumbers.map((number) => (
                <TableCell key={number} align="right" className={row.key === 'borrower-input' ? 'cost-input-cell' : undefined} sx={row.key === 'borrower-input' ? { p: 0 } : undefined}>
                    {row.key === 'borrower-input'
                        ? <EditableCurrencyCell ariaLabel={`Borrower Input DD${number}`} value={summary.borrowerInputByDrawdown[number] ?? ''} onChange={(value) => onUpdateBorrowerInputDrawdown(number, value)} displayAsNegative />
                        : moneyFormatter.format(row.values[number] || 0)}
                </TableCell>
            ))}
            <TableCell align="right">{moneyFormatter.format(row.expenditure)}</TableCell>
            <TableCell align="right">{`${row.complete.toFixed(0)}%`}</TableCell>
            <TableCell align="right">{moneyFormatter.format(row.costToComplete)}</TableCell>
        </TableRow>
    );
    return (
        <Box sx={{ p: { xs: 1, sm: 1.5 }, display: 'grid', gap: 1.5 }}>
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider' }}>
                <Table size="small" aria-label="Drawdown summary table" sx={{ ...worksheetTableSx, minWidth: minimumTableWidth }}>
                    <colgroup>
                        <col style={{ width: descriptionColumnWidth }} />
                        <col style={{ width: moneyColumnWidth }} />
                        <col style={{ width: moneyColumnWidth }} />
                        <col style={{ width: moneyColumnWidth }} />
                        {drawdownNumbers.map((number) => <col key={number} style={{ width: drawdownColumnWidth }} />)}
                        <col style={{ width: moneyColumnWidth }} />
                        <col style={{ width: percentColumnWidth }} />
                        <col style={{ width: moneyColumnWidth }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell>Summary</TableCell>
                            <TableCell align="right">Original Budget</TableCell>
                            <TableCell align="right">Add/Omit</TableCell>
                            <TableCell align="right">Final Forecast</TableCell>
                            {drawdownNumbers.map((number) => <TableCell key={number} align="right">DD{number}</TableCell>)}
                            <TableCell align="right">Total Expenditure to Date</TableCell>
                            <TableCell align="right">% Complete</TableCell>
                            <TableCell align="right">Cost to Complete</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {summary.sectionRows.map((row) => renderSummaryRow(row))}
                        {renderSummaryRow(summary.total, 'total')}
                        {renderSummaryRow(summary.borrowerInput)}
                        {renderSummaryRow(summary.facility, 'facility')}
                    </TableBody>
                </Table>
            </TableContainer>
            {sections.map((section) => (
                <TableContainer key={section.category} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table size="small" aria-label={`${section.title} drawdown table`} sx={{ ...worksheetTableSx, minWidth: minimumTableWidth }}>
                        <colgroup>
                            <col style={{ width: descriptionColumnWidth }} />
                            {[...Array(3)].map((_, index) => <col key={`money-${index}`} style={{ width: moneyColumnWidth }} />)}
                            {drawdownNumbers.map((number) => <col key={number} style={{ width: drawdownColumnWidth }} />)}
                            <col style={{ width: moneyColumnWidth }} />
                            <col style={{ width: percentColumnWidth }} />
                            <col style={{ width: moneyColumnWidth }} />
                        </colgroup>
                        <TableHead>
                            <TableRow><TableCell colSpan={7 + drawdownNumbers.length}>{section.title}</TableCell></TableRow>
                            <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell align="right">Original Budget</TableCell>
                                <TableCell align="right">Add/Omit</TableCell>
                                <TableCell align="right">Final Forecast</TableCell>
                                {drawdownNumbers.map((number) => <TableCell key={number} align="right">DD{number}</TableCell>)}
                                <TableCell align="right">Expenditure to Date</TableCell>
                                <TableCell align="right">% Complete</TableCell>
                                <TableCell align="right">Cost to Complete</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {section.rows.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell align="right">{moneyFormatter.format(row.borrower)}</TableCell>
                                    <TableCell className="cost-input-cell" sx={{ p: 0 }}>
                                        <EditableCurrencyCell ariaLabel={`Add/Omit ${row.description}`} value={Object.hasOwn(row, 'drawdown_add_omit') ? row.drawdown_add_omit : row.addOmit} onChange={(value) => onUpdateAddOmit(section.category, row.id, value)} />
                                    </TableCell>
                                    <TableCell align="right">{moneyFormatter.format(row.forecast)}</TableCell>
                                    {drawdownNumbers.map((number) => <TableCell key={number} align="right">{moneyFormatter.format(row.values[number] || 0)}</TableCell>)}
                                    <TableCell align="right">{moneyFormatter.format(row.expenditure)}</TableCell>
                                    <TableCell align="right">{`${row.complete.toFixed(0)}%`}</TableCell>
                                    <TableCell align="right">{moneyFormatter.format(row.costToComplete)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={tableTotalRowSx}>
                                <TableCell>Total</TableCell>
                                <TableCell align="right">{moneyFormatter.format(section.rows.reduce((sum, row) => sum + row.borrower, 0))}</TableCell>
                                <TableCell align="right">{moneyFormatter.format(section.rows.reduce((sum, row) => sum + row.addOmit, 0))}</TableCell>
                                <TableCell align="right">{moneyFormatter.format(section.rows.reduce((sum, row) => sum + row.forecast, 0))}</TableCell>
                                {drawdownNumbers.map((number) => <TableCell key={number} align="right">{moneyFormatter.format(section.rows.reduce((sum, row) => sum + (row.values[number] || 0), 0))}</TableCell>)}
                                <TableCell align="right">{moneyFormatter.format(section.rows.reduce((sum, row) => sum + row.expenditure, 0))}</TableCell>
                                <TableCell align="right">{`${(() => { const forecast = section.rows.reduce((sum, row) => sum + row.forecast, 0); return forecast ? (section.rows.reduce((sum, row) => sum + row.expenditure, 0) / forecast * 100).toFixed(0) : '0'; })()}%`}</TableCell>
                                <TableCell align="right">{moneyFormatter.format(section.rows.reduce((sum, row) => sum + row.costToComplete, 0))}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            ))}
        </Box>
    );
}

const XLSX_COLORS = {
    navy: 'FF17324D',
    grey: 'FF8A96A3',
    lightGrey: 'FFF1F4F7',
    paleBlue: 'FFE8F1F8',
    yellow: 'FFFACC15',
    black: 'FF111827',
    white: 'FFFFFFFF',
    border: 'FFD9DEE7'
};
const XLSX_CURRENCY_FORMAT = '£#,##0;[Red](£#,##0);-';
const XLSX_RATE_FORMAT = '£#,##0.00;[Red](£#,##0.00);-';

function styleExcelRow(row, columnCount, options = {}) {
    for (let column = 1; column <= columnCount; column += 1) {
        const cell = row.getCell(column);
        cell.font = { name: 'Aptos', size: 10, bold: Boolean(options.bold), color: { argb: options.fontColor || XLSX_COLORS.black } };
        cell.alignment = { vertical: 'middle', horizontal: column === 1 ? 'left' : (options.alignment || 'right'), wrapText: Boolean(options.wrapText) };
        cell.border = {
            top: { style: 'thin', color: { argb: XLSX_COLORS.border } },
            left: { style: 'thin', color: { argb: XLSX_COLORS.border } },
            bottom: { style: 'thin', color: { argb: XLSX_COLORS.border } },
            right: { style: 'thin', color: { argb: XLSX_COLORS.border } }
        };
        if (options.fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: options.fill } };
    }
    row.height = options.height || 20;
}

function styleExcelHeader(row, columnCount) {
    styleExcelRow(row, columnCount, { fill: XLSX_COLORS.navy, fontColor: XLSX_COLORS.white, bold: true, wrapText: true, height: 28 });
}

function styleExcelTotal(row, columnCount, fill = XLSX_COLORS.grey) {
    styleExcelRow(row, columnCount, { fill, fontColor: XLSX_COLORS.white, bold: true });
}

function formatExcelMoney(row, columns) {
    columns.forEach((column) => { row.getCell(column).numFmt = XLSX_CURRENCY_FORMAT; });
}

function populateCostExportWorkbook(output, { reference, record, workbook, drawdowns = [], interimNumber, liveDevelopmentCosts = null }) {
    const calculated = record?.calculated || {};
    output.creator = 'Monsurv';
    output.created = new Date();
    output.modified = new Date();
    output.calcProperties.fullCalcOnLoad = true;

    const overview = output.addWorksheet('Summary', { views: [{ state: 'frozen', ySplit: 2 }] });
    overview.columns = [{ width: 28 }, { width: 22 }];
    overview.mergeCells('A1:B1');
    overview.getCell('A1').value = `${reference} Project Cost Workbook`;
    styleExcelHeader(overview.getRow(1), 2);
    overview.getRow(1).height = 32;
    [
        ['Project Reference', reference],
        ['Published Revision', record?.current_revision_number || 'Draft'],
        ['Borrower Total', calculated.borrower_total || 0],
        ['Surveyor Total', calculated.surveyor_total || 0],
        ['Variance', calculated.variance || 0],
        ['Facility Headroom', calculated.facility_headroom || 0]
    ].forEach((values, index) => {
        const row = overview.addRow(values);
        styleExcelRow(row, 2, { fill: index % 2 ? XLSX_COLORS.lightGrey : undefined });
        row.getCell(1).font = { ...row.getCell(1).font, bold: true };
        if (index >= 2) row.getCell(2).numFmt = XLSX_CURRENCY_FORMAT;
    });
    overview.pageSetup = { orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1 };

    const costPlan = output.addWorksheet('Initial Cost Plan', { views: [{ state: 'frozen', ySplit: 1 }] });
    costPlan.columns = [{ width: 26 }, { width: 48 }, { width: 18 }, { width: 22 }, { width: 52 }, { width: 20, hidden: true }];
    styleExcelHeader(costPlan.addRow(['Category', 'Description', 'Borrower Budget', 'Surveyor', 'Commentary', 'Row ID']), 6);
    costPlan.autoFilter = 'A1:F1';
    for (const category of workbook.categories || []) {
        for (const rowData of category.rows || []) {
            const surveyorInput = String(rowData.surveyor_input ?? rowData.formula ?? rowData.surveyor_assessment ?? '').trim();
            const row = costPlan.addRow([
                CATEGORY_LABELS[category.key] || category.label || category.key,
                rowData.description,
                toNumber(rowData.borrower_budget),
                surveyorInput.startsWith('=') ? surveyorInput : toNumber(surveyorInput),
                rowData.commentary || '',
                rowData.id
            ]);
            styleExcelRow(row, 6, { fill: row.number % 2 ? XLSX_COLORS.lightGrey : undefined, wrapText: true });
            formatExcelMoney(row, [3]);
            if (typeof row.getCell(4).value === 'number') formatExcelMoney(row, [4]);
        }
    }
    costPlan.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };

    const benchmarking = output.addWorksheet('Benchmarking', { views: [{ state: 'frozen', ySplit: 1 }] });
    benchmarking.columns = [{ width: 30 }, { width: 24 }, { width: 24 }, { width: 24 }, { width: 24 }];
    benchmarking.mergeCells('A1:E1');
    benchmarking.getCell('A1').value = 'Cost Rate Assessment and Market Benchmarking';
    styleExcelHeader(benchmarking.getRow(1), 5);
    benchmarking.addRow([]);
    styleExcelHeader(benchmarking.addRow(['Cost Rate Assessment', 'Value', '', '', '']), 5);
    [
        ['Gross Internal Area (m²)', toNumber(workbook.benchmarking?.gia_m2)],
        ['Borrower Construction Cost (£/sq ft)', Number(calculated.borrower_build_cost_per_sq_ft || 0)],
        ['Surveyor Assessed Cost (£/sq ft)', toNumber(workbook.benchmarking?.surveyor_assessed_cost_per_sq_ft || calculated.borrower_build_cost_per_sq_ft)]
    ].forEach((values, index) => {
        const row = benchmarking.addRow(values);
        styleExcelRow(row, 5, { fill: index % 2 ? XLSX_COLORS.lightGrey : undefined });
        if (index > 0) row.getCell(2).numFmt = XLSX_RATE_FORMAT;
    });
    benchmarking.addRow([]);
    styleExcelHeader(benchmarking.addRow(['Building Function', 'Mean', 'Lower Quartile', 'Median', 'Higher Quartile']), 5);
    [
        ['BCIS £/m²', workbook.benchmarking?.bcis_mean_per_sqm, workbook.benchmarking?.bcis_lower_quartile_per_sqm, workbook.benchmarking?.bcis_median_per_sqm, workbook.benchmarking?.bcis_higher_quartile_per_sqm],
        ['BCIS £/sq ft', ...['bcis_mean_per_sqm', 'bcis_lower_quartile_per_sqm', 'bcis_median_per_sqm', 'bcis_higher_quartile_per_sqm'].map((field) => toNumber(workbook.benchmarking?.[field]) / 10.7639)]
    ].forEach((values, index) => {
        const row = benchmarking.addRow(values.map((value, column) => column ? toNumber(value) : value));
        styleExcelRow(row, 5, { fill: index % 2 ? XLSX_COLORS.lightGrey : undefined });
        [2, 3, 4, 5].forEach((column) => { row.getCell(column).numFmt = XLSX_RATE_FORMAT; });
    });
    styleExcelRow(benchmarking.addRow(['Building Function', workbook.benchmarking?.building_function || '']), 5);
    styleExcelRow(benchmarking.addRow(['Building Height', workbook.benchmarking?.building_height || 'General']), 5, { fill: XLSX_COLORS.lightGrey });
    benchmarking.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 1 };

    const drawdownNumbers = Array.from(new Set([
        ...drawdowns.map((item) => Number(item.drawdown_number)).filter(Boolean),
        Number(interimNumber) || 1
    ])).sort((a, b) => a - b);
    const sections = buildDrawdownSections(workbook, calculated, drawdowns, liveDevelopmentCosts);
    const drawdownSummary = buildDrawdownSummary(sections, drawdownNumbers, workbook.summary);
    const drawdownHeaders = ['Description', 'Original Budget', 'Add/Omit', 'Final Forecast', ...drawdownNumbers.map((number) => `DD${number}`), 'Total Expenditure to Date', '% Complete', 'Cost to Complete'];
    const drawdownSheet = output.addWorksheet('Drawdown Tables', { views: [{ state: 'frozen', ySplit: 2, xSplit: 1 }] });
    const drawdownColumnCount = drawdownHeaders.length;
    drawdownSheet.columns = [
        { width: 44 }, { width: 17 }, { width: 17 }, { width: 17 },
        ...drawdownNumbers.map(() => ({ width: 18 })),
        { width: 22 }, { width: 13 }, { width: 18 }
    ];
    drawdownSheet.mergeCells(1, 1, 1, drawdownColumnCount);
    drawdownSheet.getCell(1, 1).value = `${reference} Drawdown Summary`;
    styleExcelHeader(drawdownSheet.getRow(1), drawdownColumnCount);
    styleExcelHeader(drawdownSheet.addRow(drawdownHeaders), drawdownColumnCount);
    const addSummaryRow = (rowData, style = 'default') => {
        const values = [rowData.label, rowData.original, rowData.addOmit, rowData.forecast, ...drawdownNumbers.map((number) => rowData.values[number] || 0), rowData.expenditure, rowData.complete / 100, rowData.costToComplete];
        const row = drawdownSheet.addRow(values);
        if (style === 'total') styleExcelTotal(row, drawdownColumnCount);
        else if (style === 'facility') styleExcelTotal(row, drawdownColumnCount, XLSX_COLORS.navy);
        else styleExcelRow(row, drawdownColumnCount, { fill: row.number % 2 ? XLSX_COLORS.lightGrey : undefined });
        formatExcelMoney(row, [...Array(3 + drawdownNumbers.length).keys()].map((index) => index + 2).concat([drawdownColumnCount - 2, drawdownColumnCount]));
        row.getCell(drawdownColumnCount - 1).numFmt = '0%';
        return row;
    };
    drawdownSummary.sectionRows.forEach((row) => addSummaryRow(row));
    const totalRow = addSummaryRow(drawdownSummary.total, 'total');
    const acknowledged = Math.round(toNumber(workbook.summary?.drawdown_add_omit_acknowledged_total) * 100) / 100;
    if (drawdownSummary.total.addOmit !== 0 && acknowledged !== Math.round(drawdownSummary.total.addOmit * 100) / 100) {
        totalRow.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: XLSX_COLORS.yellow } };
        totalRow.getCell(3).font = { name: 'Aptos', size: 10, bold: true, color: { argb: XLSX_COLORS.black } };
        totalRow.getCell(3).note = 'The Add/Omit Column is not balanced to £0, is this correct?';
    }
    const borrowerInputRow = addSummaryRow(drawdownSummary.borrowerInput);
    [2, ...drawdownNumbers.map((_, index) => index + 5)].forEach((column) => {
        borrowerInputRow.getCell(column).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: XLSX_COLORS.paleBlue } };
    });
    addSummaryRow(drawdownSummary.facility, 'facility');

    for (const section of sections) {
        drawdownSheet.addRow([]);
        const sectionTitle = drawdownSheet.addRow([section.title]);
        drawdownSheet.mergeCells(sectionTitle.number, 1, sectionTitle.number, drawdownColumnCount);
        styleExcelHeader(sectionTitle, drawdownColumnCount);
        styleExcelHeader(drawdownSheet.addRow(drawdownHeaders), drawdownColumnCount);
        for (const rowData of section.rows) {
            const row = drawdownSheet.addRow([rowData.description, rowData.borrower, rowData.addOmit, rowData.forecast, ...drawdownNumbers.map((number) => rowData.values[number] || 0), rowData.expenditure, rowData.complete / 100, rowData.costToComplete]);
            styleExcelRow(row, drawdownColumnCount, { fill: row.number % 2 ? XLSX_COLORS.lightGrey : undefined });
            formatExcelMoney(row, [...Array(3 + drawdownNumbers.length).keys()].map((index) => index + 2).concat([drawdownColumnCount - 2, drawdownColumnCount]));
            row.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: XLSX_COLORS.paleBlue } };
            row.getCell(drawdownColumnCount - 1).numFmt = '0%';
        }
        const sectionTotal = {
            label: 'Total',
            original: sumRows(section.rows, 'borrower'),
            addOmit: sumRows(section.rows, 'addOmit'),
            forecast: sumRows(section.rows, 'forecast'),
            values: Object.fromEntries(drawdownNumbers.map((number) => [number, section.rows.reduce((sum, row) => sum + toNumber(row.values[number]), 0)])),
            expenditure: sumRows(section.rows, 'expenditure'),
            complete: 0,
            costToComplete: sumRows(section.rows, 'costToComplete')
        };
        sectionTotal.complete = sectionTotal.forecast > 0 ? sectionTotal.expenditure / sectionTotal.forecast * 100 : 0;
        addSummaryRow(sectionTotal, 'total');
    }
    drawdownSheet.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };

    const metadata = output.addWorksheet('_Monsurv');
    metadata.state = 'veryHidden';
    metadata.addRows([['schema_version', 'project-cost-v1'], ['project_reference', reference]]);
    return output;
}

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function createRow() {
    return {
        id: window.crypto?.randomUUID?.() || `row-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        description: 'New cost item',
        borrower_budget: '',
        surveyor_assessment: '',
        surveyor_input: '',
        formula: '',
        commentary: '',
        source: { system: 'surveyor' }
    };
}

function decorateWorkbook(workbook) {
    const next = clone(workbook);
    next.categories = (next.categories || []).map((category) => ({
        ...category,
        rows: (category.rows || []).map((row) => ({
            ...row,
            surveyor_input: String(row.formula || (row.surveyor_assessment ?? ''))
        }))
    }));
    return next;
}

function prepareWorkbook(workbook) {
    const next = clone(workbook);
    next.categories = (next.categories || []).map((category) => ({
        ...category,
        rows: (category.rows || []).map(({ surveyor_input, ...row }) => {
            const input = String(surveyor_input ?? '').trim();
            return {
                ...row,
                formula: input.startsWith('=') ? input : '',
                surveyor_assessment: input.startsWith('=') ? row.surveyor_assessment : input
            };
        })
    }));
    return next;
}

function Metric({ label, value, tone = '#0f766e' }) {
    return (
        <Box sx={{ minWidth: 0, borderTop: `3px solid ${tone}`, pt: 1 }}>
            <Typography sx={{ fontSize: '0.67rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
            <Typography sx={{ mt: 0.35, fontSize: '1.25rem', fontWeight: 900, whiteSpace: 'nowrap' }}>{moneyFormatter.format(Number(value || 0))}</Typography>
        </Box>
    );
}

export default function ProjectCostDashboard({ reference, interimNumber, drawdownsEnabled = true, liveDevelopmentCosts = null, aiBenchmark = null }) {
    const importRef = useRef(null);
    const historyRef = useRef([]);
    const futureRef = useRef([]);
    const editSequenceRef = useRef(0);
    const savingRef = useRef(false);
    const pendingSaveRef = useRef(false);
    const [saveTick, setSaveTick] = useState(0);
    const [record, setRecord] = useState(null);
    const [workbook, setWorkbook] = useState(null);
    const [revisions, setRevisions] = useState([]);
    const [drawdowns, setDrawdowns] = useState([]);
    const [drawdownsLoading, setDrawdownsLoading] = useState(false);
    const [drawdownsError, setDrawdownsError] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saveState, setSaveState] = useState('Saved');
    const [dirty, setDirty] = useState(false);
    const [tab, setTab] = useState('plan');
    const [publishOpen, setPublishOpen] = useState(false);
    const [publishReason, setPublishReason] = useState('');
    const [publishing, setPublishing] = useState(false);
    const [exporting, setExporting] = useState(false);

    async function load() {
        setLoading(true);
        setError('');
        try {
            const [workbookResult, revisionResult] = await Promise.all([
                fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/cost-workbook?mode=surveyor`),
                fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/cost-workbook/revisions?mode=surveyor`)
            ]);
            setRecord(workbookResult.workbook);
            setWorkbook(decorateWorkbook(workbookResult.workbook.workbook));
            setRevisions(revisionResult.revisions || []);
            setDirty(false);
            setSaveState('Saved');
            historyRef.current = [];
            futureRef.current = [];
        } catch (requestError) {
            setError(requestError?.message || 'Project costs could not be loaded.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { void load(); }, [reference]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!drawdownsEnabled && tab === 'drawdowns') setTab('plan');
    }, [drawdownsEnabled, tab]);

    useEffect(() => {
        if (tab !== 'drawdowns') return undefined;
        let cancelled = false;
        setDrawdownsLoading(true);
        setDrawdownsError('');
        fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/drawdown-history`)
            .then((result) => {
                if (!cancelled) setDrawdowns(Array.isArray(result?.items) ? result.items : []);
            })
            .catch((requestError) => {
                if (!cancelled) setDrawdownsError(requestError?.message || 'Drawdown history could not be loaded.');
            })
            .finally(() => {
                if (!cancelled) setDrawdownsLoading(false);
            });
        return () => { cancelled = true; };
    }, [reference, tab]);

    useEffect(() => {
        if (!dirty || !workbook || !record) return undefined;
        const timer = window.setTimeout(async () => {
            if (savingRef.current) {
                pendingSaveRef.current = true;
                return;
            }
            savingRef.current = true;
            const sequence = editSequenceRef.current;
            setSaveState('Saving');
            try {
                const result = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/cost-workbook?mode=surveyor`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ lock_version: record.lock_version, workbook: prepareWorkbook(workbook) })
                });
                setRecord(result.workbook);
                if (sequence === editSequenceRef.current) {
                    setWorkbook(decorateWorkbook(result.workbook.workbook));
                    setDirty(false);
                    setSaveState('Saved');
                } else {
                    pendingSaveRef.current = true;
                }
                setError('');
            } catch (requestError) {
                setSaveState(requestError?.status === 409 ? 'Conflict' : 'Save failed');
                setError(requestError?.message || 'Project costs could not be saved.');
            } finally {
                savingRef.current = false;
                if (pendingSaveRef.current) {
                    pendingSaveRef.current = false;
                    setSaveTick((value) => value + 1);
                }
            }
        }, 700);
        return () => window.clearTimeout(timer);
    }, [dirty, record, reference, saveTick, workbook]);

    function commit(nextWorkbook) {
        historyRef.current = [...historyRef.current.slice(-49), clone(workbook)];
        futureRef.current = [];
        editSequenceRef.current += 1;
        setWorkbook(nextWorkbook);
        setDirty(true);
        setSaveState('Unsaved changes');
    }

    function updateCategoryRows(categoryKey, rows) {
        const next = clone(workbook);
        next.categories = next.categories.map((category) => category.key === categoryKey ? { ...category, rows } : category);
        commit(next);
    }

    function updateNested(section, field, value) {
        const next = clone(workbook);
        next[section] = { ...(next[section] || {}), [field]: value };
        commit(next);
    }

    function updateDrawdownAddOmit(categoryKey, rowId, value) {
        const next = clone(workbook);
        if (String(rowId).startsWith('facility-balance-')) {
            next.summary = {
                ...(next.summary || {}),
                facility_balance_add_omit: { ...(next.summary?.facility_balance_add_omit || {}), [categoryKey]: value }
            };
        } else {
            next.categories = next.categories.map((category) => category.key === categoryKey
                ? { ...category, rows: category.rows.map((row) => row.id === rowId ? { ...row, drawdown_add_omit: value } : row) }
                : category);
        }
        next.summary = { ...(next.summary || {}), drawdown_add_omit_acknowledged_total: '' };
        commit(next);
    }

    function updateBorrowerInputDrawdown(drawdownNumber, value) {
        const next = clone(workbook);
        next.summary = {
            ...(next.summary || {}),
            borrower_input_drawdowns: {
                ...(next.summary?.borrower_input_drawdowns || {}),
                [drawdownNumber]: value
            }
        };
        commit(next);
    }

    function undo() {
        const previous = historyRef.current.pop();
        if (!previous) return;
        futureRef.current.push(clone(workbook));
        editSequenceRef.current += 1;
        setWorkbook(previous);
        setDirty(true);
        setSaveState('Unsaved changes');
    }

    function redo() {
        const next = futureRef.current.pop();
        if (!next) return;
        historyRef.current.push(clone(workbook));
        editSequenceRef.current += 1;
        setWorkbook(next);
        setDirty(true);
        setSaveState('Unsaved changes');
    }

    async function publish() {
        if (dirty || savingRef.current) {
            setError('Wait for the working draft to finish saving before publishing.');
            return;
        }
        setPublishing(true);
        try {
            await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/cost-workbook/publish?mode=surveyor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lock_version: record.lock_version, reason: publishReason })
            });
            setPublishOpen(false);
            setPublishReason('');
            await load();
        } catch (requestError) {
            setError(requestError?.message || 'The cost revision could not be published.');
        } finally {
            setPublishing(false);
        }
    }

    async function exportWorkbook() {
        try {
            setExporting(true);
            setError('');
            const ExcelJS = await import('exceljs');
            const output = new ExcelJS.Workbook();
            let exportDrawdowns = drawdowns;
            try {
                const drawdownResult = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/drawdown-history`);
                exportDrawdowns = Array.isArray(drawdownResult?.items) ? drawdownResult.items : drawdowns;
            } catch {
                // Export the already-loaded history if the refresh endpoint is temporarily unavailable.
            }
            populateCostExportWorkbook(output, { reference, record, workbook, drawdowns: exportDrawdowns, interimNumber, liveDevelopmentCosts });
            const buffer = await output.xlsx.writeBuffer();
            const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `${reference}-Cost-Plan.xlsx`;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (exportError) {
            setError(exportError?.message || 'The XLSX workbook could not be exported.');
        } finally {
            setExporting(false);
        }
    }

    async function importWorkbook(file) {
        if (!file) return;
        try {
            const ExcelJS = await import('exceljs');
            const input = new ExcelJS.Workbook();
            await input.xlsx.load(await file.arrayBuffer());
            const sheet = input.getWorksheet('Initial Cost Plan') || input.getWorksheet('Cost Plan');
            if (!sheet) throw new Error('The workbook must contain an Initial Cost Plan sheet.');
            const next = clone(workbook);
            const imported = new Map(Object.keys(CATEGORY_LABELS).map((key) => [key, []]));
            const categoryLookup = new Map(Object.entries(CATEGORY_LABELS).flatMap(([key, label]) => [[key.toLowerCase(), key], [label.toLowerCase(), key]]));
            sheet.eachRow((excelRow, rowNumber) => {
                if (rowNumber === 1) return;
                const categoryValue = String(excelRow.getCell(1).text || '').trim().toLowerCase();
                const categoryKey = categoryLookup.get(categoryValue);
                if (!categoryKey || !imported.has(categoryKey)) throw new Error(`Unsupported category on row ${rowNumber}.`);
                const description = String(excelRow.getCell(2).text || '').trim();
                if (!description) return;
                const surveyorInput = String(excelRow.getCell(4).text || '').trim();
                imported.get(categoryKey).push({
                    id: String(excelRow.getCell(6).text || '').trim() || createRow().id,
                    description,
                    borrower_budget: excelRow.getCell(3).value ?? '',
                    surveyor_assessment: surveyorInput.startsWith('=') ? '' : surveyorInput,
                    surveyor_input: surveyorInput,
                    formula: surveyorInput.startsWith('=') ? surveyorInput : '',
                    commentary: String(excelRow.getCell(5).text || '').trim(),
                    source: { system: 'xlsx_import' }
                });
            });
            next.categories = next.categories.map((category) => ({ ...category, rows: imported.get(category.key) || [] }));
            commit(next);
            setError('');
        } catch (importError) {
            setError(importError?.message || 'The XLSX workbook could not be imported.');
        } finally {
            if (importRef.current) importRef.current.value = '';
        }
    }

    if (loading) return <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}><CircularProgress aria-label="Loading project costs" /></Box>;
    if (!workbook) return <Alert severity="info">{error || 'Section 7 cost data is not available for this project yet.'}</Alert>;

    const calculated = record?.calculated || {};
    const sourceChangeCount = record?.source_changes?.length || 0;
    const constructionRows = (workbook.categories?.find((category) => category.key === 'construction')?.rows || [])
        .filter((row) => row.id !== 'surveyor-benchmark-assessment');
    const areaSqFt = toNumber(workbook.benchmarking?.gia_m2) * 10.7639;
    const constructionTotal = Number(calculated.borrower_category_totals?.construction || 0);
    const bespokeRowIds = Array.isArray(workbook.benchmarking?.bespoke_row_ids) ? workbook.benchmarking.bespoke_row_ids : [];

    return (
        <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ md: 'center' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900 }}>Project Costs</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">{reference}</Typography>
                        <Chip size="small" label={record.current_revision_number ? `Published revision ${record.current_revision_number}` : 'Unpublished draft'} />
                        <Chip size="small" color={saveState === 'Saved' ? 'success' : saveState === 'Conflict' ? 'error' : 'default'} label={saveState} />
                        {sourceChangeCount ? <Chip size="small" color="warning" label={`${sourceChangeCount} source change${sourceChangeCount === 1 ? '' : 's'}`} /> : null}
                    </Stack>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Tooltip title="Undo"><span><IconButton onClick={undo} disabled={!historyRef.current.length}><UndoIcon /></IconButton></span></Tooltip>
                    <Tooltip title="Redo"><span><IconButton onClick={redo} disabled={!futureRef.current.length}><RedoIcon /></IconButton></span></Tooltip>
                    <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => importRef.current?.click()}>Import XLSX</Button>
                    <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={exporting} onClick={() => void exportWorkbook()}>{exporting ? 'Exporting…' : 'Export XLSX'}</Button>
                    <Button size="small" variant="contained" startIcon={<PublishIcon />} onClick={() => setPublishOpen(true)}>Publish revision</Button>
                    <input ref={importRef} hidden type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => void importWorkbook(event.target.files?.[0])} />
                </Stack>
            </Stack>

            {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
                    <Metric label="Borrower Total" value={calculated.borrower_total} tone="#64748b" />
                    <Metric label="Surveyor Total" value={calculated.surveyor_total} tone="#0f766e" />
                    <Metric label="Variance" value={calculated.variance} tone={Number(calculated.variance || 0) > 0 ? '#dc2626' : '#16a34a'} />
                    <Metric label="Facility Headroom" value={calculated.facility_headroom} tone={Number(calculated.facility_headroom || 0) < 0 ? '#dc2626' : '#2563eb'} />
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Project cost views">
                    <Tab value="plan" label="Initial Cost Plan" />
                    <Tab value="benchmarking" label="Benchmarking" />
                    <Tab value="drawdowns" label="Drawdown Tables" disabled={!drawdownsEnabled} title={drawdownsEnabled ? '' : 'Available after the Initial Report is issued, the project is confirmed live, and the first interim site visit is booked.'} />
                    <Tab value="changes" label={`Source Changes${sourceChangeCount ? ` (${sourceChangeCount})` : ''}`} />
                    <Tab value="revisions" label={`Revisions (${revisions.length})`} />
                </Tabs>
                <Divider />

                {tab === 'plan' ? (
                    <Box sx={{ p: { xs: 1, sm: 1.5 }, display: 'grid', gap: 1.5 }}>
                        {(workbook.categories || []).map((category) => (
                            <Box key={category.key} sx={{ minWidth: 0 }}>
                                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                                    <Table
                                        size="small"
                                        aria-label={`${CATEGORY_LABELS[category.key] || category.label} cost table`}
                                        sx={worksheetTableSx}
                                    >
                                        <colgroup>
                                            <col />
                                            <col style={{ width: 132 }} />
                                            <col style={{ width: 132 }} />
                                            <col style={{ width: 132 }} />
                                            <col style={{ width: '28%' }} />
                                            <col style={{ width: 42 }} />
                                        </colgroup>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ py: 1 }}>
                                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                        <Typography sx={{ fontWeight: 900 }}>{CATEGORY_LABELS[category.key] || category.label}</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                                                            {moneyFormatter.format(Number(calculated.surveyor_category_totals?.[category.key] || 0))}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ width: '36%', fontWeight: 900 }}>Description</TableCell>
                                                <TableCell align="right" sx={{ width: 132, fontWeight: 900 }}>Borrower</TableCell>
                                                <TableCell align="right" sx={{ width: 132, fontWeight: 900 }}>Surveyor</TableCell>
                                                <TableCell align="right" sx={{ width: 132, fontWeight: 900 }}>Difference</TableCell>
                                                <TableCell sx={{ fontWeight: 900 }}>Commentary</TableCell>
                                                <TableCell sx={{ width: 42, p: 0 }} aria-label="Row actions" />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(category.rows || []).map((row, index) => {
                                                const borrowerValue = Number(row.borrower_budget || 0);
                                                const surveyorValue = Number(calculated.row_values?.[row.id] ?? row.surveyor_assessment ?? borrowerValue);
                                                const difference = surveyorValue - borrowerValue;
                                                return (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell className="cost-input-cell" sx={{ p: 0 }}>
                                                            <InputBase
                                                                fullWidth
                                                                aria-label="Description"
                                                                value={row.description || ''}
                                                                onChange={(event) => {
                                                                    const rows = clone(category.rows); rows[index].description = event.target.value; updateCategoryRows(category.key, rows);
                                                                }}
                                                                sx={{ px: 1.5, py: 0.85, fontSize: '0.9rem' }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ py: 0.85, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                            {moneyFormatter.format(borrowerValue)}
                                                        </TableCell>
                                                        <TableCell className="cost-input-cell" sx={{ p: 0 }}>
                                                            <CurrencyFormulaInput
                                                                ariaLabel="Surveyor assessment or formula"
                                                                value={row.surveyor_input}
                                                                onChange={(value) => {
                                                                    const rows = clone(category.rows); rows[index].surveyor_input = value; updateCategoryRows(category.key, rows);
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ py: 0.85, fontWeight: difference ? 800 : 500, color: difference > 0 ? 'error.main' : difference < 0 ? 'success.main' : 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                            {moneyFormatter.format(difference)}
                                                        </TableCell>
                                                        <TableCell className="cost-input-cell" sx={{ p: 0 }}>
                                                            <InputBase
                                                                fullWidth
                                                                aria-label="Amendment commentary"
                                                                placeholder="Add commentary"
                                                                value={row.commentary || ''}
                                                                onChange={(event) => {
                                                                    const rows = clone(category.rows); rows[index].commentary = event.target.value; updateCategoryRows(category.key, rows);
                                                                }}
                                                                sx={{ px: 1.5, py: 0.85, fontSize: '0.9rem' }}
                                                            />
                                                        </TableCell>
                                                        <TableCell padding="checkbox">
                                                            <Tooltip title="Delete row">
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label={`Delete ${row.description || 'cost'} row`}
                                                                    disabled={row.id === 'surveyor-benchmark-assessment'}
                                                                    onClick={() => updateCategoryRows(category.key, category.rows.filter((candidate) => candidate.id !== row.id))}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            <TableRow sx={tableTotalRowSx}>
                                                <TableCell sx={{ fontWeight: 900 }}>Category total</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                                                    {moneyFormatter.format(Number(calculated.borrower_category_totals?.[category.key] || 0))}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                                                    {moneyFormatter.format(Number(calculated.surveyor_category_totals?.[category.key] || 0))}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                                                    {moneyFormatter.format(Number(calculated.surveyor_category_totals?.[category.key] || 0) - Number(calculated.borrower_category_totals?.[category.key] || 0))}
                                                </TableCell>
                                                <TableCell colSpan={2} />
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => updateCategoryRows(category.key, [...(category.rows || []), createRow()])} sx={{ mt: 0.75 }}>Add row</Button>
                            </Box>
                        ))}
                    </Box>
                ) : null}

                {tab === 'benchmarking' ? (
                    <Box sx={{ p: { xs: 1, sm: 1.5 }, display: 'grid', gap: 2 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Cost Rate Assessment</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25, mb: 1.5 }}>
                                <TextField size="small" label="Gross Internal Area (m²)" value={workbook.benchmarking?.gia_m2 ?? ''} onChange={(event) => updateNested('benchmarking', 'gia_m2', event.target.value)} />
                                <TextField size="small" label="Borrower Construction Cost (£/sq ft)" value={calculated.borrower_build_cost_per_sq_ft == null ? '' : rateFormatter.format(calculated.borrower_build_cost_per_sq_ft)} disabled />
                                <EditableRateInput value={workbook.benchmarking?.surveyor_assessed_cost_per_sq_ft} fallback={calculated.borrower_build_cost_per_sq_ft} onChange={(value) => updateNested('benchmarking', 'surveyor_assessed_cost_per_sq_ft', value)} />
                            </Box>
                            <TableContainer sx={{ border: '1px solid', borderColor: 'divider' }}>
                                <Table size="small" aria-label="Construction cost benchmarking analysis" sx={worksheetTableSx}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell rowSpan={2} sx={{ width: '34%' }}>Element</TableCell>
                                            <TableCell colSpan={2} align="center">Cost Estimate</TableCell>
                                            <TableCell rowSpan={2} align="center" sx={{ width: 86 }}>Bespoke</TableCell>
                                            <TableCell colSpan={2} align="center">Benchmarking Analysis</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell align="right" sx={{ width: 135 }}>Value</TableCell>
                                            <TableCell align="right" sx={{ width: 82 }}>%</TableCell>
                                            <TableCell align="right" sx={{ width: 150 }}>Bespoke Items £/ft²</TableCell>
                                            <TableCell align="right" sx={{ width: 165 }}>Construction Cost £/ft²</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {constructionRows.map((row) => {
                                            const value = toNumber(row.borrower_budget);
                                            const bespoke = bespokeRowIds.includes(row.id);
                                            return (
                                                <TableRow key={row.id}>
                                                    <TableCell>{row.description}</TableCell>
                                                    <TableCell align="right">{moneyFormatter.format(value)}</TableCell>
                                                    <TableCell align="right">{constructionTotal ? `${((value / constructionTotal) * 100).toFixed(2)}%` : '0.00%'}</TableCell>
                                                    <TableCell align="center"><Checkbox size="small" checked={bespoke} onChange={(event) => updateNested('benchmarking', 'bespoke_row_ids', event.target.checked ? [...bespokeRowIds, row.id] : bespokeRowIds.filter((id) => id !== row.id))} /></TableCell>
                                                    <TableCell align="right">{bespoke && areaSqFt ? rateFormatter.format(value / areaSqFt) : '—'}</TableCell>
                                                    <TableCell align="right">{!bespoke && areaSqFt ? rateFormatter.format(value / areaSqFt) : '—'}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        <TableRow sx={tableTotalRowSx}>
                                            <TableCell>Total</TableCell>
                                            <TableCell align="right">{moneyFormatter.format(constructionTotal)}</TableCell>
                                            <TableCell align="right">100.00%</TableCell>
                                            <TableCell />
                                            <TableCell align="right">{areaSqFt ? rateFormatter.format(constructionRows.filter((row) => bespokeRowIds.includes(row.id)).reduce((sum, row) => sum + toNumber(row.borrower_budget), 0) / areaSqFt) : '—'}</TableCell>
                                            <TableCell align="right">{areaSqFt ? rateFormatter.format(constructionRows.filter((row) => !bespokeRowIds.includes(row.id)).reduce((sum, row) => sum + toNumber(row.borrower_budget), 0) / areaSqFt) : '—'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        <TableContainer sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <Table size="small" aria-label="Development cost rate comparison" sx={worksheetTableSx}>
                                <TableHead><TableRow><TableCell>Description</TableCell><TableCell align="right">Borrower Assessment</TableCell><TableCell align="right">Surveyor Assessment</TableCell><TableCell align="right">Difference</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {[
                                        ['Construction Cost per sq ft', calculated.borrower_build_cost_per_sq_ft, String(workbook.benchmarking?.surveyor_assessed_cost_per_sq_ft ?? '').trim() || calculated.borrower_build_cost_per_sq_ft],
                                        ['Construction Cost per sqm', Number(calculated.borrower_build_cost_per_sq_ft || 0) * 10.7639, Number(String(workbook.benchmarking?.surveyor_assessed_cost_per_sq_ft ?? '').trim() || calculated.borrower_build_cost_per_sq_ft || 0) * 10.7639]
                                    ].map(([label, borrower, surveyor]) => <TableRow key={label}><TableCell>{label}</TableCell><TableCell align="right">{rateFormatter.format(Number(borrower || 0))}</TableCell><TableCell align="right">{rateFormatter.format(Number(surveyor || 0))}</TableCell><TableCell align="right">{rateFormatter.format(Number(surveyor || 0) - Number(borrower || 0))}</TableCell></TableRow>)}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Market Benchmarking</Typography>
                            <TableContainer sx={{ border: '1px solid', borderColor: 'divider' }}>
                                <Table size="small" aria-label="BCIS market benchmarking" sx={worksheetTableSx}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '22%' }}>Building Function</TableCell>
                                            <TableCell colSpan={4} sx={{ p: 0.75 }}>
                                                <TextField fullWidth size="small" aria-label="Building Function" value={workbook.benchmarking?.building_function ?? ''} onChange={(event) => updateNested('benchmarking', 'building_function', event.target.value)} sx={{ bgcolor: '#fff' }} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Building Height</TableCell>
                                            <TableCell align="center">Mean</TableCell>
                                            <TableCell align="center">Lower Quartile</TableCell>
                                            <TableCell align="center">Median</TableCell>
                                            <TableCell align="center">Higher Quartile</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell rowSpan={2} sx={{ p: 0.75, verticalAlign: 'top' }}>
                                                <TextField select fullWidth size="small" aria-label="Building Height" value={workbook.benchmarking?.building_height || 'General'} onChange={(event) => updateNested('benchmarking', 'building_height', event.target.value)}>
                                                    {['General', 'Single Storey', '2-Storey', '3-Storey', '4-Storey+'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                                                </TextField>
                                            </TableCell>
                                            {[
                                                ['Mean', 'bcis_mean_per_sqm'],
                                                ['Lower Quartile', 'bcis_lower_quartile_per_sqm'],
                                                ['Median', 'bcis_median_per_sqm'],
                                                ['Higher Quartile', 'bcis_higher_quartile_per_sqm']
                                            ].map(([label, field]) => (
                                                <TableCell key={field} sx={{ p: 0 }}>
                                                    <InputBase fullWidth aria-label={`${label} (£/m²)`} placeholder="£/m² entry" value={workbook.benchmarking?.[field] ?? ''} onChange={(event) => updateNested('benchmarking', field, event.target.value)} inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }} sx={{ px: 1, py: 0.85 }} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            {['bcis_mean_per_sqm', 'bcis_lower_quartile_per_sqm', 'bcis_median_per_sqm', 'bcis_higher_quartile_per_sqm'].map((field) => (
                                                <TableCell key={field} align="right" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                                                    {String(workbook.benchmarking?.[field] ?? '').trim() ? `${rateFormatter.format(toNumber(workbook.benchmarking[field]) / 10.7639)} /sq ft` : 'Auto-calculated £/sq ft'}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Internal Benchmarking</Typography>
                            <Paper variant="outlined" sx={{ minHeight: 96, borderRadius: 1.5, bgcolor: '#fafafa' }} />
                        </Box>
                        <AiBenchmarking proposal={aiBenchmark} />
                        <TextField label="Benchmarking Commentary" multiline minRows={3} value={workbook.benchmarking?.commentary ?? ''} onChange={(event) => updateNested('benchmarking', 'commentary', event.target.value)} />
                    </Box>
                ) : null}

                {tab === 'drawdowns' ? (
                    <DrawdownTables
                        workbook={workbook}
                        calculated={calculated}
                        drawdowns={drawdowns}
                        loading={drawdownsLoading}
                        error={drawdownsError}
                        interimNumber={interimNumber}
                        liveDevelopmentCosts={liveDevelopmentCosts}
                        onUpdateAddOmit={updateDrawdownAddOmit}
                        onUpdateAddOmitAcknowledgement={(value) => updateNested('summary', 'drawdown_add_omit_acknowledged_total', value ?? '')}
                        onUpdateBorrowerInput={(value) => updateNested('summary', 'borrower_input', value)}
                        onUpdateBorrowerInputDrawdown={updateBorrowerInputDrawdown}
                    />
                ) : null}

                {tab === 'changes' ? (
                    <Stack spacing={1} sx={{ p: 2 }}>
                        {sourceChangeCount ? record.source_changes.map((change) => (
                            <Paper key={change.field_path} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: alpha('#f59e0b', 0.05) }}>
                                <Typography sx={{ fontWeight: 800 }}>{change.source_value?.description || change.current_value?.description || change.field_path}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Borrower source changed from {moneyFormatter.format(Number(change.current_value?.borrower_budget || 0))} to {moneyFormatter.format(Number(change.source_value?.borrower_budget || 0))}.
                                </Typography>
                            </Paper>
                        )) : <Typography color="text.secondary">The working draft matches the latest questionnaire source.</Typography>}
                    </Stack>
                ) : null}

                {tab === 'revisions' ? (
                    <Stack spacing={1} sx={{ p: 2 }}>
                        {revisions.length ? revisions.map((revision) => (
                            <Paper key={revision.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Box><Typography sx={{ fontWeight: 900 }}>Revision {revision.revision_number}</Typography><Typography variant="body2" color="text.secondary">{revision.publication_reason}</Typography></Box>
                                    <Typography variant="body2" color="text.secondary">{new Date(revision.published_at).toLocaleString('en-GB')}</Typography>
                                </Stack>
                            </Paper>
                        )) : <Typography color="text.secondary">No cost revision has been published yet.</Typography>}
                    </Stack>
                ) : null}
            </Paper>

            <Dialog open={publishOpen} onClose={() => !publishing && setPublishOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Publish cost revision</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Published revisions are immutable and can be bound to generated reports.</Typography>
                    <TextField autoFocus fullWidth multiline minRows={3} label="Reason for publication" value={publishReason} onChange={(event) => setPublishReason(event.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPublishOpen(false)} disabled={publishing}>Cancel</Button>
                    <Button variant="contained" onClick={() => void publish()} disabled={publishing || !publishReason.trim()}>{publishing ? 'Publishing...' : 'Publish'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export { buildDrawdownSections, buildDrawdownSummary, createRow, decorateWorkbook, formatCurrencyCell, populateCostExportWorkbook, prepareWorkbook };